import { JobsStatus } from "../constants/Jobs";

/**
 * Doubles single quotes so a value can be embedded in a SQL string literal.
 * Required for every user-supplied value that reaches a where clause.
 */
export const escapeSqlLiteral = (value: string): string => value.replace(/'/g, "''");

/**
 * Escapes a user-supplied LIKE term. Backslash is escaped first because it is the
 * ESCAPE character and must not be applied to the escapes we are about to add.
 * Clauses emitting this MUST pair it with ESCAPE '\'.
 *
 * Without this, a `%` typed into the search box would match every row.
 */
export const escapeLikeTerm = (term: string): string =>
    escapeSqlLiteral(term.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_"));

export interface JobsQueryParams {
    /** Includes the synthetic Alle (-1) and Hjelpepunkt (-2) values. */
    status: JobsStatus;
    /** Raw user input from the search box. */
    searchText: string;
    /** Username for "Mine jobber"; null for the "Alle" tab. */
    owner: string | null;
    /** Globalids of Plan jobs belonging to the active version; empty when none. */
    planGlobalIds: string[];
}

/** Above this many ids the IN list is dropped rather than emitted. See buildPlanGateClause. */
export const MAX_PLAN_GATE_IDS = 1000;

const DATE_CUTOFF = "opprettet_dato >= date '2026-01-01'";
const HJELPEPUNKT_PATTERN = "%HJELPEPUNKT%";
const SEARCH_FIELDS = ["navn", "sjomerke_nr", "beskrivelse"];

/**
 * True for the statuses whose result set can contain a Plan row, and which therefore
 * need the version gate. Exported so callers can skip the Steps prefilter query
 * entirely when it cannot affect the outcome.
 */
export const planGateApplies = (status: JobsStatus): boolean =>
    status === JobsStatus.Alle || status === JobsStatus.Hjelpepunkt || status === JobsStatus.Plan;

const tokenize = (searchText: string): string[] =>
    searchText
        .trim()
        .split(/\s+/)
        .filter((token) => token.length > 0);

const searchClause = (token: string): string => {
    const term = escapeLikeTerm(token.toUpperCase());
    const fields = SEARCH_FIELDS.map(
        (field) => `UPPER(${field}) LIKE '%${term}%' ESCAPE '\\'`
    );
    return `(${fields.join(" OR ")})`;
};

const buildPlanGateClause = (status: JobsStatus, planGlobalIds: string[]): string | null => {
    if (!planGateApplies(status)) {
        return null;
    }

    if (planGlobalIds.length > MAX_PLAN_GATE_IDS) {
        console.warn(
            `Jobber: ${planGlobalIds.length} planjobber i versjonen overstiger grensen på ` +
                `${MAX_PLAN_GATE_IDS}; hopper over versjonsfilteret for planjobber.`
        );
        return null;
    }

    const inList = planGlobalIds.map((id) => `'${escapeSqlLiteral(id)}'`).join(",");

    if (status === JobsStatus.Plan) {
        // No plans in this version means no rows at all, not "all plans".
        return planGlobalIds.length === 0 ? "1=0" : `globalid IN (${inList})`;
    }

    // status IS NULL is kept because <> against NULL yields NULL, which would drop the row.
    if (planGlobalIds.length === 0) {
        return `(status IS NULL OR status <> ${JobsStatus.Plan})`;
    }
    return `(status IS NULL OR status <> ${JobsStatus.Plan} OR globalid IN (${inList}))`;
};

/**
 * Composes the full where clause for a jobs query. Pure: no I/O, no ArcGIS.
 *
 * Clause order is stable so the output is testable by exact string comparison:
 * date cutoff, status/hjelpepunkt, plan gate, owner, then one clause per search token.
 */
export const buildJobsWhere = (params: JobsQueryParams): string => {
    const clauses: string[] = [DATE_CUTOFF];

    if (params.status === JobsStatus.Hjelpepunkt) {
        // The synthetic bucket ignores status entirely, matching the current client behaviour.
        clauses.push(`UPPER(navn) LIKE '${HJELPEPUNKT_PATTERN}'`);
    } else {
        if (params.status !== JobsStatus.Alle) {
            clauses.push(`status = ${params.status}`);
        }
        // navn IS NULL is kept because NOT LIKE against NULL yields NULL, which would
        // drop unnamed jobs from every list. The client code coerced null to "".
        clauses.push(`(navn IS NULL OR UPPER(navn) NOT LIKE '${HJELPEPUNKT_PATTERN}')`);
    }

    const planGate = buildPlanGateClause(params.status, params.planGlobalIds);
    if (planGate) {
        clauses.push(planGate);
    }

    if (params.owner) {
        clauses.push(`opprettet_av = '${escapeSqlLiteral(params.owner)}'`);
    }

    for (const token of tokenize(params.searchText)) {
        clauses.push(searchClause(token));
    }

    return clauses.join(" AND ");
};
